import { DocumentNode, gql, useMutation } from '@apollo/client';
import { MenuItem, useDisclosure, useToast } from '@chakra-ui/react';
import * as React from 'react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PaywallModal } from '@/components/Paywall';
import { logError } from '@/imports/logging/ClientLogger';
import { XIcon } from '@/imports/ui/chakra/feather';

import {
  DeletePostDropdownItem_DeletePost,
  DeletePostDropdownItem_DeletePostVariables,
} from './graphql-types/DeletePostDropdownItem_DeletePost';
import { DeletePostDropdownItem_PostFragment } from './graphql-types/DeletePostDropdownItem_PostFragment';

import { useUpdateCacheOnPostNotVisible } from '../../hooks/useUpdateCacheOnPostNotVisible';
import { useSponsorPaywallModal } from './hooks/useSponsorPaywallModal';

const POST_FRAGMENT = gql`
  fragment DeletePostDropdownItem_PostFragment on PostV2 {
    _id
    ...UseUpdateCacheOnPostNotVisible_PostV2Fragment
    currentUserCanDeletePost
    currentUserCanAttemptToDeletePostWithOrganizationSponsorshipPaywall
    organization {
      _id
    }
  }
  ${useUpdateCacheOnPostNotVisible.postFragment}
`;

const DELETE_POST = gql`
  mutation DeletePostDropdownItem_DeletePost($input: DeletePostInput_V2!) {
    softDeletePostV2(input: $input)
  }
`;

export const DeletePostDropdownItem: React.FC<{
  post: DeletePostDropdownItem_PostFragment;
}> & {
  postFragment: DocumentNode;
} = ({ post }) => {
  const confirmDialog = useDisclosure();
  const paywall = useDisclosure();
  const toast = useToast();
  const { updateCacheOnPostNotVisible } = useUpdateCacheOnPostNotVisible();

  const { title, subtitle, shouldShowUpgradeCTA } = useSponsorPaywallModal({
    organizationId: post.organization?._id,
  });

  const [deletePost] = useMutation<
    DeletePostDropdownItem_DeletePost,
    DeletePostDropdownItem_DeletePostVariables
  >(DELETE_POST, {
    refetchQueries: ['DraftsPage_GetMyDraftPosts', 'AccountMenu_Query'],
  });

  const deletePostConfirm = async (): Promise<void> => {
    try {
      await deletePost({
        variables: {
          input: {
            id: post._id,
          },
        },
        update(cache) {
          updateCacheOnPostNotVisible({ cache, post });
        },
      });
      toast({ title: 'Successfully deleted the post.', status: 'success' });
    } catch (err) {
      toast({
        title: 'Failed to delete post.',
        status: 'error',
      });
      logError(err);
    }
  };

  const handleDeleteClick = (): void => {
    if (
      !post.currentUserCanDeletePost &&
      post.currentUserCanAttemptToDeletePostWithOrganizationSponsorshipPaywall
    ) {
      paywall.onOpen();
    } else {
      confirmDialog.onOpen();
    }
  };

  const showDeleteItem =
    post.currentUserCanDeletePost ||
    post.currentUserCanAttemptToDeletePostWithOrganizationSponsorshipPaywall;

  if (!showDeleteItem) {
    return null;
  }

  return (
    <>
      <MenuItem icon={<XIcon />} onClick={handleDeleteClick} color="red">
        Delete
      </MenuItem>
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={confirmDialog.onClose}
        title="Delete this Post"
        body="Are you sure you want to permanently remove this post?"
        confirmButtonText="Delete Post"
        onConfirm={deletePostConfirm}
      />
      <PaywallModal
        isOpen={paywall.isOpen}
        onClose={paywall.onClose}
        organizationId={post.organization?._id ?? ''}
        headerText={title ?? ''}
        bodyText={subtitle ?? ''}
        shouldShowUpgradeCTA={shouldShowUpgradeCTA}
        source="delete post paywall"
      />
    </>
  );
};

DeletePostDropdownItem.postFragment = POST_FRAGMENT;
